import {
  Disclosure as DisclosureWrapper,
  DisclosureButton,
  DisclosurePanel,
  Transition,
} from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

import Typography from '~/shared/components/Typography';

import { ClearFilterBadge } from './FilterButton';

interface DisclosureItem {
  label: string;
  onClick: () => void;
}

interface DisclosureProps {
  title?: string;
  count?: number;
  badgeLabel?: string;
  items?: DisclosureItem[];
  open: boolean; // controlled mode only
  onOpenChange: (isOpen: boolean) => void; // controlled mode only
  onClearFilters?: () => void; // function to clear all filters of this type
  className?: string;
  buttonClassName?: string;
  panelClassName?: string;
  itemClassName?: string;
  chevronClassName?: string;
  children: React.ReactNode;
}

export default function FiltersDisclosure({
  title,
  count,
  badgeLabel,
  open,
  onOpenChange,
  onClearFilters,
  className = '',
  buttonClassName = '',
  panelClassName = '',
  chevronClassName = '',
  children,
}: DisclosureProps): React.ReactElement {
  const handleDisclosureClick = () => {
    onOpenChange(!open);
  };

  const showBadge = (count !== undefined && count > 0) || Boolean(badgeLabel);

  const handleCountClick = (e?: React.MouseEvent | React.KeyboardEvent) => {
    if (e) {
      e.stopPropagation();
    }
    if (onClearFilters && showBadge) {
      onClearFilters();
    }
  };

  return (
    <div className={clsx('w-full max-w-md  ', className)}>
      <DisclosureWrapper>
        <DisclosureButton
          onClick={handleDisclosureClick}
          className={clsx('w-full text-left flex justify-between items-center ', buttonClassName)}
        >
          <div className="flex items-center justify-between gap-2 w-full">
            <Typography
              fontWeight={600}
              variant="p2"
              color="font-family-outfit text-primary-900  cursor-pointer py-3"
            >
              {title}
            </Typography>
            {showBadge && (
              <ClearFilterBadge
                count={count}
                label={badgeLabel}
                handleClick={handleCountClick}
                title={`Clear ${title} filters`}
              />
            )}
          </div>
          <ChevronDownIcon
            className={clsx(
              'ml-2 w-[16px] h-[16px] text-gray-600 stroke-2  cursor-pointer transition-transform duration-200 ease-in-out',
              open ? 'rotate-180' : '',
              chevronClassName
            )}
          />
        </DisclosureButton>

        <Transition
          show={open}
          enter="transition-all duration-200 ease-out"
          enterFrom="max-h-0 opacity-0"
          enterTo="max-h-[500px] opacity-100"
          leave="transition-all duration-200 ease-in"
          leaveFrom="max-h-[500px] opacity-100"
          leaveTo="max-h-0 opacity-0"
        >
          <DisclosurePanel static className={clsx('overflow-hidden flex flex-col', panelClassName)}>
            {children}
          </DisclosurePanel>
        </Transition>
      </DisclosureWrapper>
    </div>
  );
}
